/**
 * The ONE place the press that checks what a graph rests on is priced, refused and
 * taken.
 *
 * The thin cue carries it as a descriptor (`checkResting`), so the cue cannot spend
 * (invariant 9); this is the other half, `promotePress.ts`'s shape for the same
 * reason. What the button names, the figure it prints, the sentence it refuses with
 * and the run it starts all come from `restingPlan` — one reading, so the label
 * cannot name three claims while the run checks two, and the CHEAPEST lens cannot
 * sort on a figure the button does not print.
 *
 * The press is a checks-only RUN (`RunSpec.checkTargets`), not three checks fired in
 * a row, so the spending ceiling, its reserve, Stop, the ledger and `endsTheRun`
 * hold over it without a line of their own. The claims are `restingTargets`: most
 * beneath first, by count, never by a rating.
 *
 * A `.ts` file rather than a component, so the refusals and the plan are tested
 * without a renderer, and the sentence the card prints and the toast says are
 * provably one string.
 */

import { providerFor, resolveFor, useStore } from '../core/store'
import { RESTING_CHECKS } from '../core/cues'
import { DEFAULT_EVIDENCE_PROFILE, normalizeEvidenceProfile } from '../core/epistemic'
import { parseCap } from '../core/ledger'
import { fmtUsd } from '../core/models'
import { DEMO_SESSION_ID } from '../core/persist'
import type { ReasonNode, RunBudget } from '../core/types'
import {
  budgetForEvidenceProfile,
  checksOnlySpec,
  dearestPass,
  evidenceProfileCeiling,
  projectRunPressUsd,
  rolesInPlay,
  runModelsFor,
  startRun,
  type RunSpec,
} from '../engine/run'
import { voiceOn } from '../core/voice'
import { searchlessCheckSentence } from '../core/corpus'
import { passDrawsVersion } from '../core/passDraw'
import { restingTargets } from '../engine/runPolicy'
import { reasonKeyLine } from './pressName'

/** The store's shape, without exporting `State` from the store for it. */
type StoreState = ReturnType<typeof useStore.getState>

/** What the plan reads: the graph, the routes and the ceiling the next run gets. */
export type RestingPlanView = Pick<StoreState, 'session' | 'modelId' | 'roleModels' | 'runCeiling'>

/** Everything a refusal reads besides: keys, the one search preference, the locks. */
export type RestingView = RestingPlanView &
  Pick<StoreState, 'keys' | 'customBase' | 'customModel' | 'preferCorpus' | 'run' | 'busy'>

export interface RestingPlan {
  /** Most beneath first, at most `RESTING_CHECKS`, each with what sits beneath it.
   *  Empty when nothing qualifies. */
  targets: { node: ReasonNode; below: number }[]
  /** The run the press starts, and the budget `startRun` will hold it to — both
   *  null with nothing to check. */
  spec: RunSpec | null
  budget: RunBudget | null
  /** The ceiling that run is held to: the stricter of the composer's, which every
   *  run is given, and the exploration's evidence protocol. */
  ceiling: number | null
  /** The run projection for exactly that budget — an estimate that leans high — or
   *  null where a model in play has no list rate, or there is nothing to check. */
  est: number | null
}

let memo: {
  session: RestingPlanView['session']
  modelId: RestingPlanView['modelId']
  roleModels: RestingPlanView['roleModels']
  runCeiling: RestingPlanView['runCeiling']
  voice: boolean
  draws: number
  plan: RestingPlan
} | null = null

/**
 * The claims, the run and its figure.
 *
 * Sized exactly as `startRun` sizes it — `budgetForEvidenceProfile` under the
 * exploration's evidence protocol and the stricter ceiling — and priced by
 * `projectRunPressUsd` over that budget with no scan in play, the projection every
 * run's quote goes through, the voice's wrap-up included while it is on. Memoized
 * on its six inputs, because the coach's price table asks on every store write.
 */
export function restingPlan(s: RestingPlanView): RestingPlan {
  if (
    memo &&
    memo.session === s.session &&
    memo.modelId === s.modelId &&
    memo.roleModels === s.roleModels &&
    memo.runCeiling === s.runCeiling &&
    // The quote includes the voice's wrap-up while the voice is on (D295).
    memo.voice === voiceOn() &&
    // And its checks are drawn from the passes this browser has seen (D311).
    memo.draws === passDrawsVersion()
  ) {
    return memo.plan
  }
  const targets = restingTargets(s.session, RESTING_CHECKS)
  const profile = normalizeEvidenceProfile(
    s.session.research?.evidenceProfile ?? DEFAULT_EVIDENCE_PROFILE,
  )
  const ceiling = evidenceProfileCeiling(parseCap(s.runCeiling), profile)
  let plan: RestingPlan = { targets, spec: null, budget: null, ceiling, est: null }
  if (targets.length) {
    const spec = checksOnlySpec(
      targets.map((t) => t.node.id),
      parseCap(s.runCeiling),
    )
    const budget = budgetForEvidenceProfile(spec, profile, ceiling)
    plan = {
      targets,
      spec,
      budget,
      ceiling,
      est: projectRunPressUsd(s, runModelsFor(s), budget, null, false),
    }
  }
  memo = {
    session: s.session,
    modelId: s.modelId,
    roleModels: s.roleModels,
    runCeiling: s.runCeiling,
    voice: voiceOn(),
    draws: passDrawsVersion(),
    plan,
  }
  return plan
}

/** The button's words for this many claims. Built here and nowhere else, so the card
 *  and the digest row cannot name a different count from the run they start. With
 *  nothing to name it keeps the descriptor's own count, and the refusal says why. */
export function restingLabel(n: number): string {
  if (n === 1) return 'Check the claim most of this rests on'
  return `Check the ${n > 0 ? n : RESTING_CHECKS} claims most of this rests on`
}

/** The figure beside the words, in the form the press promises. Null where there is
 *  no figure — no figure beats a wrong one. */
export function restingEstimate(plan: RestingPlan): string | null {
  return plan.est === null ? null : `est. ${fmtUsd(plan.est)}`
}

/** The claims, named, for the button's title: which ones, how much rests on each,
 *  and that the press is a run under the ceiling. */
export function restingTitle(plan: RestingPlan): string {
  if (!plan.targets.length) return ''
  const named = plan.targets.map((t) => `“${t.node.label}” (${t.below} below)`).join(' · ')
  return `One source check on each, most beneath first: ${named}. It runs as a checks-only run, so your ceiling, Stop and the run account all apply. An estimate that leans high.`
}

/**
 * Why the press cannot run, in the sentence the reader gets — or null when it can.
 *
 * The refusals `startRun` would issue that are knowable at the press, asked with the
 * engine's own functions, plus the two only this press has: nothing left to check,
 * and the worked example. Asked BEFORE the coach retires the cue, for the reason
 * `cueRefusal` gives. The check role's key comes before the reasoning one: every pass
 * this run makes is a check, so that is the key the reader needs named — and it is a
 * routed role, so it names its own provider and never falls back (invariant 4).
 */
export function restingRefusal(s: RestingView): string | null {
  if (s.busy || (s.run !== null && s.run.phase !== 'ended')) {
    return 'A pass is already going. Stop it, or let it finish.'
  }
  if (s.session.research?.mathematicsInvalid || s.session.research?.mathematics?.enabled) {
    return 'Mathematical mode runs its declared agenda. Queue source checks there instead.'
  }
  // The worked example was written in advance, and a run over it would check claims
  // nobody here made. The demo carries no verification, ever.
  if (s.session.id === DEMO_SESSION_ID) {
    return 'The worked example was written in advance. Explore an idea of your own, and this checks what it rests on.'
  }
  const plan = restingPlan(s)
  if (!plan.targets.length || !plan.budget) {
    return 'Nothing here is left to check: every claim something rests on has a live source check, or is folded, closed or a question.'
  }
  const check = resolveFor(s, 'check')
  if (!check) {
    return `A source check searches the live web, so it needs your ${providerFor(s, 'check').name} key.`
  }
  // "Prefer my sources" is what makes a searchless model usable for a check, and
  // `startRun` reads it the same way.
  if (check.entry.search === 'none' && !s.preferCorpus) {
    return searchlessCheckSentence(check.entry.name)
  }
  if (!resolveFor(s, 'reason')) {
    // The route's model and house in D202's sentence, as the coach's other
    // keyless line says it (`reasonKeyLine`): "for the model you selected" was
    // false of a routed reasoning role, whose model nobody selected.
    return `${reasonKeyLine(s)} This press starts a run, and a run needs it even when it only checks sources.`
  }
  if (plan.ceiling !== null) {
    // `startRun`'s two ceiling refusals, over the roles THIS run can bill: the
    // reasoning route it is stamped with and the check route every pass goes to.
    const models = runModelsFor(s)
    const roles = rolesInPlay(plan.budget, false)
    const unpriced = [models.reason, ...(roles.check ? [models.check] : [])].find((e) => !e.price)
    if (unpriced) {
      return `A spending ceiling needs a published price, and ${unpriced.name} runs on an endpoint you supplied. Clear the ceiling, or switch models.`
    }
    const floor = dearestPass(models, null, roles)
    if (floor && plan.ceiling < floor.usd) {
      return `${fmtUsd(plan.ceiling)} cannot be guaranteed to cover a single pass on ${floor.entry.name}, so the run would stop before its first check. ${fmtUsd(floor.usd)} is the smallest ceiling that can.`
    }
  }
  return null
}

/**
 * The press.
 *
 * Refused out loud if anything above holds — the card has already greyed out on the
 * same reading, so this is for a press that was legal when it rendered and is not
 * by the time it lands. Otherwise it starts the run the plan describes, and every
 * guard `startRun` keeps still stands behind it.
 */
export function takeRestingPress(): void {
  const s = useStore.getState()
  const refusal = restingRefusal(s)
  if (refusal) {
    s.toast(refusal, 'warn')
    return
  }
  const plan = restingPlan(s)
  if (plan.spec) void startRun(plan.spec)
}
